import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Grid } from '@mui/system';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { FutureShareValue, PresentShareValue } from 'api';
import { DECIMAL_PLACES } from 'common/config/app';
import { ADJUSTED_EBITDA_TITLE } from 'common/constants/financials';
import { SAFE_DECIMAL_PLACES } from 'common/constants/general';
import { VALUATIONS_PAGE_KEY, VALUATIONS_PAGE_VALUE } from 'common/constants/notes';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useStore } from 'common/store';
import { ConfigurationSpreadsheets, ScalarSpreadsheetConfigs } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { CustomContainer } from 'components';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import { useLayoutContext } from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import getRowConfig from 'pages/ValuationsAllocation/approaches/FutureExit/ValuesPerShareBase/config/rowConfig';
import { ALLOCATION_SCENARIO_METHOD_OPM } from 'pages/ValuationsAllocation/common/constants/allocation';
import {
  ALLOCATION_METHOD_OPTIONS_VALUES,
  AllocationMethodsMapValues,
  FUTURE_EXIT_SLUG,
  METRIC_OPTION_LABELS,
  SCENARIO_VALUES_DISPATCH_CELLS,
  SPECIFIED_MULTIPLE,
} from 'pages/ValuationsAllocation/common/constants/futureExit';
import {
  FE_ALLOCATION_METHOD_SPREADSHEET_ALLOCATION_METHOD,
  FE_ALLOCATION_METHOD_SPREADSHEET_CAP_TABLE,
  FE_ALLOCATION_METHOD_SPREADSHEET_MATURITY_YEARS,
  FE_ALLOCATION_METHOD_SPREADSHEET_VOLATILITY,
  FE_FUTURE_EQUITY_SPREADSHEET_FUTURE_VALUE_CURRENT_SHAREHOLDERS,
  FE_FUTURE_EQUITY_SPREADSHEET_VALUATION_APPROACH,
  FE_MODIFIED_PRESENT_EQUITY_VALUE_SPREADSHEET_DISCOUNT_RATE,
} from 'pages/ValuationsAllocation/common/constants/futureExit/sheetAliases';
import { FUTURE_EQUITY_SPREADSHEET_TABLE_NAMES } from 'pages/ValuationsAllocation/common/constants/futureExit/sheetConfigs';
import { Note } from 'pages/ValuationsAllocation/types';
import {
  getApproachTableName,
  getFutureExitScenarioMethod,
  getSelectionCellOptions,
  parseCellValue,
  SpecificApproach,
} from 'pages/ValuationsAllocation/util';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { AllocationService } from 'services';
import { useNotes } from 'services/hooks/notes';
import { dbShortDate, getArrayValue, getNumberValue, getObjectValue, getStringValue, isValidDate } from 'utilities';
import { AllocationMethodTable } from './AllocationMethodTable';
import { FUTURE_EXIT } from './AllocationMethodTable/utils/constants';
import { FutureEquity } from './FutureEquity';
import { getApproachFromColumn } from './FutureEquity/config/utils/getApproachFromColumn';
import { ModifiedPresentEquity } from './ModifiedPresentEquity';
import { FutureExitParams, SecurityWithValue, ValidateParams, WriteScenarioValuesToApproachProps } from './types';
import FutureExitContext from './utils/FutureExitContext';
import { FUTURE_VALUES_PER_SHARE_TABLE_SLUG } from './ValuesPerShareBase/config/constants';
import createColumns from './ValuesPerShareBase/config/createColumns';
import useValuesPerShareTableData from './ValuesPerShareBase/useValuesPerShareTableData';
import { ValuesPerShareTable } from './ValuesPerShareTable';
import WidgetDrawer from '../../../../components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from '../../../../components/Drawer/widget-drawer/WidgetDrawer.props';

const {
  FUTURE_EQUITY_VALUE,
  MODIFIED_PRESENT_EQUITY_VALUE,
  PRESENT_VALUES_PER_SHARE,
  ALLOCATION_METHOD_VALUE_WATERFALL_CSE,
} = FUTURE_EQUITY_SPREADSHEET_TABLE_NAMES;

const VALUATION_FUTURE_EXIT_FUTURE_EQUITY_VALUE_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${FUTURE_EXIT_SLUG}-${FUTURE_EQUITY_VALUE}-container`;
const VALUATION_FUTURE_EXIT_MODIFIED_PRESENT_EQUITY_VALUE_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${FUTURE_EXIT_SLUG}-${MODIFIED_PRESENT_EQUITY_VALUE}-container`;
const VALUATION_FUTURE_EXIT_ALLOCATION_METHOD_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${FUTURE_EXIT_SLUG}-${ALLOCATION_METHOD_VALUE_WATERFALL_CSE}-container`;
const VALUATION_FUTURE_EXIT_FUTURE_VALUES_PER_SHARE_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${FUTURE_EXIT_SLUG}-${FUTURE_VALUES_PER_SHARE_TABLE_SLUG}-container`;
const VALUATION_FUTURE_EXIT_PRESENT_VALUES_PER_SHARE_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${FUTURE_EXIT_SLUG}-${PRESENT_VALUES_PER_SHARE}-container`;

const FutureExit = (params: FutureExitParams) => {
  const { spreadsheets, onChange: onWorkbookChange, approaches, approach, financials, isDisabled, workbook } = params;
  const { valuations_approach_future_exit: valuationsApproachFutureExit } = getObjectValue(approach);
  const {
    allocation_method: allocationMethod,
    cap_table: capTable,
    exit_date: exitDate,
    future_share_values: futureShareValues,
    present_share_values: presentShareValues,
  } = getObjectValue(valuationsApproachFutureExit);

  const approachPanelId = approach.panelId;
  const getApproach = useCallback(
    () => getApproachFromColumn(getObjectValue(valuationsApproachFutureExit), approaches),
    [approaches, valuationsApproachFutureExit]
  );

  const getValuesPerShare = (dataShareValues?: PresentShareValue[] | FutureShareValue[]): SecurityWithValue[] => {
    if (!dataShareValues) {
      return [];
    }
    return dataShareValues.map(item => ({
      security: {
        id: item.security,
        name: getStringValue(item.security_name),
      },
      value: getNumberValue(item.value),
    }));
  };

  const { futureExitScenarioMethod } = getFutureExitScenarioMethod(valuationsApproachFutureExit);

  const [currentAllocationMethodValue, setCurrentAllocationMethodValue] = useState<any>(null);
  const initialPresentValuesPerShare = getValuesPerShare(presentShareValues);
  const [presentValuesPerShare, setPresentValuesPerShare] = useState<SecurityWithValue[]>(initialPresentValuesPerShare);

  const initialFutureValuesPerShare = getValuesPerShare(futureShareValues);
  const [futureValuesPerShare, setFutureValuesPerShare] = useState<SecurityWithValue[]>(initialFutureValuesPerShare);

  const [selectedApproach, setSelectedApproach] = useState(getApproach());
  const { notes, setNotes, notesHasChanged, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const { presentValueSheet, futureEquitySheet, allocationMethodOPMSheet } = spreadsheets;
  const { setNotesInApproach, updateApproachesScenarioMethods, marketDate, resetConfiguration } =
    useContext(ValuationContext);
  const { isExportMode } = useLayoutContext();
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { capTableList } = storeValue;
  const {
    fieldAttributes: { futureShareValueAttrs, presentShareValueAttrs },
  } = futureEquitySheet;

  const { futureValuesPerShareSheet, presentValuesPerShareSheet } = useValuesPerShareTableData({
    approach,
    futureValuesPerShare,
    presentValuesPerShare,
  });

  const futureShareValueDecimalPlaces = useMemo(
    () => futureShareValueAttrs?.value.decimal_places ?? SAFE_DECIMAL_PLACES,
    [futureShareValueAttrs]
  );
  const presentShareValueDecimalPlaces = useMemo(
    () => presentShareValueAttrs?.value.decimal_places ?? SAFE_DECIMAL_PLACES,
    [presentShareValueAttrs]
  );

  const valuesPerShareSpreadsheets = useMemo(
    () =>
      futureValuesPerShareSheet && presentValuesPerShareSheet
        ? [futureValuesPerShareSheet, presentValuesPerShareSheet]
        : [],
    [futureValuesPerShareSheet, presentValuesPerShareSheet]
  );

  const { onChange: valuesPerShareOnChange, workbook: valuesPerShareWorkbook } =
    useWorkbook(valuesPerShareSpreadsheets);

  const multipleOptions = useMemo(() => {
    if (selectedApproach) {
      return [
        ...getSelectionCellOptions({ specificApproach: selectedApproach as SpecificApproach }),
        SPECIFIED_MULTIPLE,
      ];
    }
    return [SPECIFIED_MULTIPLE];
  }, [selectedApproach]);

  const metricOptions = useMemo(() => {
    if (!financials?.use_adjusted_ebitda) {
      return METRIC_OPTION_LABELS.filter(m => !m.includes(ADJUSTED_EBITDA_TITLE));
    }
    return METRIC_OPTION_LABELS;
  }, [financials]);

  const updateCurrentAllocationMethodValue = useCallback(
    (newAllocationMethod: ConfigurationSpreadsheets) => {
      newAllocationMethod.reverseShouldValidate();
      if (currentAllocationMethodValue) {
        currentAllocationMethodValue.reverseShouldValidate();
      }
      setCurrentAllocationMethodValue(newAllocationMethod);
    },
    [currentAllocationMethodValue]
  );

  const contextValue = useMemo(
    () => ({
      approaches,
      multipleOptions,
      metricOptions,
      currentAllocationMethodValue,
      setCurrentAllocationMethodValue: updateCurrentAllocationMethodValue,
    }),
    [approaches, multipleOptions, metricOptions, currentAllocationMethodValue, updateCurrentAllocationMethodValue]
  );

  const getCellValue = useCallback((tableSpreadSheet: ScalarSpreadsheetConfigs, key: string) => {
    const { cells } = tableSpreadSheet;
    return Object.values(cells).find((cell: ICell) => cell?.alias === key || cell?.customKey === key);
  }, []);

  const getValueFromTable = useCallback(
    (tableSpreadSheet: ScalarSpreadsheetConfigs, key: string, fieldDecimalPlaces: number = DECIMAL_PLACES) => {
      const cell = getCellValue(tableSpreadSheet, key);
      return cell?.value ? parseCellValue({ cell, fieldDecimalPlaces }) : null;
    },
    [getCellValue]
  );

  const hasValidParams = useCallback((props: ValidateParams, isOPM: boolean): boolean => {
    const commonProperties = ['scenarioMethod', 'captableId', 'exitDate', 'discountRate', 'equityValue'];
    const propertiesToValidate = isOPM ? [...commonProperties, 'maturity', 'volatility'] : commonProperties;
    const invalidProperties = propertiesToValidate.filter(
      property =>
        props[property] === null ||
        props[property] === undefined ||
        (Number(props[property]) === 0 && property !== 'scenarioMethod') ||
        (typeof props[property] === 'string' && props[property].trim() === '') ||
        (Number.isNaN(Number(props[property])) && !isValidDate(props[property]))
    );
    return invalidProperties.length === 0;
  }, []);

  const getScenarioValuesParams = useCallback(
    (scenarioMethod: number, isOPM: boolean, capTableSelection: number | null) => {
      if (!futureEquitySheet || !presentValueSheet || !allocationMethodOPMSheet) {
        return {};
      }
      // Future Values Table
      const equityValue = getValueFromTable(
        futureEquitySheet,
        FE_FUTURE_EQUITY_SPREADSHEET_FUTURE_VALUE_CURRENT_SHAREHOLDERS
      );
      const discountRate = getValueFromTable(
        presentValueSheet,
        FE_MODIFIED_PRESENT_EQUITY_VALUE_SPREADSHEET_DISCOUNT_RATE
      );

      const maturityValue = getValueFromTable(
        allocationMethodOPMSheet,
        FE_ALLOCATION_METHOD_SPREADSHEET_MATURITY_YEARS
      );
      const volatilityValue = getValueFromTable(allocationMethodOPMSheet, FE_ALLOCATION_METHOD_SPREADSHEET_VOLATILITY);

      return {
        marketDate,
        captableId: capTableSelection ?? capTable,
        discountRate,
        equityValue,
        exitDate: dbShortDate(exitDate),
        maturity: isOPM ? maturityValue : null,
        presentValues: null,
        scenarioMethod,
        scenarioType: FUTURE_EXIT,
        volatility: isOPM ? volatilityValue : null,
      };
    },
    [marketDate, futureEquitySheet, presentValueSheet, allocationMethodOPMSheet, getValueFromTable, capTable, exitDate]
  );
  const findExistingShareValueIdBySecurity = useCallback(
    (securityId: number, shareValues: PresentShareValue[] | FutureShareValue[]) => {
      const shareValue = shareValues.find(sv => sv.security === securityId);
      return shareValue?.id;
    },
    []
  );

  const generateShareValuesFromResponse = useCallback(
    (
      responseShareValues: SecurityWithValue[],
      shareValues: PresentShareValue[] | FutureShareValue[],
      futureExitId: number | null,
      decimalPlaces: number
    ) =>
      responseShareValues.map(sv => {
        const shareValueId = findExistingShareValueIdBySecurity(sv.security.id, shareValues);
        return {
          id: shareValueId,
          security: sv.security.id,
          value: Number(sv.value).toFixed(decimalPlaces),
          future_exit: futureExitId,
        };
      }),
    [findExistingShareValueIdBySecurity]
  );

  const writeScenariosValuesToApproachData = useCallback(
    ({ presentValues, futureValues, isOPM }: WriteScenarioValuesToApproachProps) => {
      if (valuationsApproachFutureExit) {
        const futureExitId = valuationsApproachFutureExit.id ?? null;
        // delete share values for securities not present in the response
        const recentlyUsedSecurities = presentValues.map(pv => pv.security.id);
        valuationsApproachFutureExit.deleted_present_share_value_ids = (presentShareValues ?? [])
          .filter(psv => !recentlyUsedSecurities.includes(psv.security) && psv.id)
          .map(({ id }) => id) as number[];
        if (isOPM) {
          // OPM future share values makes no sense, so delete whatever exists (futureValues is empty)
          valuationsApproachFutureExit.deleted_future_share_value_ids = (
            valuationsApproachFutureExit.future_share_values ?? []
          )
            .filter(fsv => fsv.id)
            .map(({ id }) => id) as number[];
        } else {
          valuationsApproachFutureExit.deleted_future_share_value_ids = (futureShareValues ?? [])
            .filter(fsv => !recentlyUsedSecurities.includes(fsv.security) && fsv.id)
            .map(({ id }) => id) as number[];
        }
        // create/update share values
        valuationsApproachFutureExit.present_share_values = generateShareValuesFromResponse(
          presentValues,
          presentShareValues as PresentShareValue[],
          futureExitId,
          presentShareValueDecimalPlaces
        );
        valuationsApproachFutureExit.future_share_values = generateShareValuesFromResponse(
          futureValues,
          futureShareValues as FutureShareValue[],
          futureExitId,
          futureShareValueDecimalPlaces
        );
      }
    },
    [
      valuationsApproachFutureExit,
      presentShareValues,
      futureShareValues,
      generateShareValuesFromResponse,
      futureShareValueDecimalPlaces,
      presentShareValueDecimalPlaces,
    ]
  );

  const updatePresentValuesPerShareSheet = useCallback(
    (presentValues: SecurityWithValue[]) => {
      const presentValuesPerShareTableName = getApproachTableName({
        approach,
        tableSuffix: FUTURE_EQUITY_SPREADSHEET_TABLE_NAMES.PRESENT_VALUES_PER_SHARE,
      });
      const presentValuesPerShareColumns = createColumns(presentValues);
      workbook[presentValuesPerShareTableName]?.reset({
        columns: presentValuesPerShareColumns,
        rowConfig: getRowConfig({
          securities: presentValues,
          isFutureValuesPerShare: false,
        }),
      });
      if (resetConfiguration) {
        resetConfiguration();
      }
    },
    [approach, workbook, resetConfiguration]
  );

  const getScenarioValues = useCallback(
    async (scenarioMethod: number, capTableSelection: number | null) => {
      const isOPM = scenarioMethod === ALLOCATION_SCENARIO_METHOD_OPM;
      const scenarioValuesParams = getScenarioValuesParams(scenarioMethod, isOPM, capTableSelection);
      if (hasValidParams(scenarioValuesParams, isOPM)) {
        const allocationService = new AllocationService();
        const response = await allocationService.postScenarioValues(scenarioValuesParams);
        if (response) {
          const { present_values: presentValues, future_values: futureValues } = response as {
            present_values: SecurityWithValue[];
            future_values: SecurityWithValue[];
          };
          setPresentValuesPerShare(presentValues);
          setFutureValuesPerShare(futureValues);
          writeScenariosValuesToApproachData({ presentValues, futureValues, isOPM });
          updatePresentValuesPerShareSheet(presentValues);
        }
      } else if (isOPM && !hasValidParams(scenarioValuesParams, isOPM) && presentValuesPerShare.length !== 0) {
        setPresentValuesPerShare([]);
        setFutureValuesPerShare([]);
      }
    },
    [
      getScenarioValuesParams,
      hasValidParams,
      presentValuesPerShare.length,
      writeScenariosValuesToApproachData,
      updatePresentValuesPerShareSheet,
    ]
  );

  useEffect(() => {
    if (currentAllocationMethodValue) {
      updateApproachesScenarioMethods();
    }
  }, [currentAllocationMethodValue, updateApproachesScenarioMethods]);

  const onChange = useCallback(
    async (cell: ICell, expr: string) => {
      onWorkbookChange(cell, expr);
      if (cell.alias === FE_FUTURE_EQUITY_SPREADSHEET_VALUATION_APPROACH) {
        const foundApproach = approaches.find(a => (a.id?.toString() ?? a.panelId) === expr?.toString());
        setSelectedApproach(foundApproach);
      }
      if (SCENARIO_VALUES_DISPATCH_CELLS.includes(cell.alias ?? '')) {
        const allocationMethodValue =
          cell.alias === FE_ALLOCATION_METHOD_SPREADSHEET_ALLOCATION_METHOD ? cell.value : allocationMethod;
        const scenarioMethod =
          typeof allocationMethodValue === 'number'
            ? allocationMethodValue
            : ALLOCATION_METHOD_OPTIONS_VALUES[allocationMethodValue as keyof AllocationMethodsMapValues];
        getScenarioValues(scenarioMethod, null);
      } else if (cell.alias === FE_ALLOCATION_METHOD_SPREADSHEET_CAP_TABLE) {
        const newCapTableSelection = getArrayValue(capTableList).find(({ id }) => id === Number(expr));
        if (newCapTableSelection) {
          getScenarioValues(allocationMethod as number, newCapTableSelection.id as number);
        }
      }
    },
    [approaches, getScenarioValues, onWorkbookChange, allocationMethod, capTableList]
  );

  useEffect(() => {
    if (!isEmpty(notes)) {
      setNotesInApproach?.(prevState => {
        const tmpNotes = prevState.filter((note: Note) => note.panelId !== approachPanelId);
        return [...tmpNotes, { notes, panelId: approachPanelId, notesHasChanged } as Note];
      });
    }
  }, [notes, notesHasChanged, approachPanelId, setNotesInApproach]);

  useExportSignal({
    target: PdfExportTargets.FUTURE_EXIT.id,
    status: !futureEquitySheet?.data || !presentValueSheet?.data ? 'loading' : 'ready',
    enabled: isExportMode,
  });

  if (isExportMode) {
    return (
      <FutureExitContext.Provider value={contextValue}>
        <Box data-ref={PdfExportTargets.FUTURE_EXIT.id} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Left: equity value summaries */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FutureEquity spreadsheets={spreadsheets} onChange={onChange} />
            <ModifiedPresentEquity spreadsheets={spreadsheets} onChange={onChange} />
          </Box>
          {/* Right: allocation method inputs + per-share values output */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <AllocationMethodTable
              approach={valuationsApproachFutureExit}
              spreadsheets={spreadsheets}
              onChange={onChange}
              isDisabled={isDisabled}
              approaches={approaches}
              financials={financials}
            />
            {Boolean(futureValuesPerShare.length) && futureValuesPerShareSheet && (
              <ValuesPerShareTable
                onChange={valuesPerShareOnChange}
                valuesPerShareSheet={futureValuesPerShareSheet}
                workbook={valuesPerShareWorkbook}
              />
            )}
            {Boolean(presentValuesPerShare.length) && presentValuesPerShareSheet && (
              <ValuesPerShareTable
                onChange={valuesPerShareOnChange}
                valuesPerShareSheet={presentValuesPerShareSheet}
                workbook={valuesPerShareWorkbook}
              />
            )}
          </Box>
        </Box>
      </FutureExitContext.Provider>
    );
  }
  return (
    <FutureExitContext.Provider value={contextValue}>
      <CustomContainer>
        {/* Future equity value */}
        <Grid className={VALUATION_FUTURE_EXIT_FUTURE_EQUITY_VALUE_CONTAINER_CLASS}>
          <FutureEquity spreadsheets={spreadsheets} onChange={onChange} />
        </Grid>

        {/* Modified present equity value */}
        <Grid className={VALUATION_FUTURE_EXIT_MODIFIED_PRESENT_EQUITY_VALUE_CONTAINER_CLASS}>
          <ModifiedPresentEquity spreadsheets={spreadsheets} onChange={onChange} />
        </Grid>

        {/* Allocation method */}
        <Grid
          className={clsx(VALUATION_FUTURE_EXIT_ALLOCATION_METHOD_CONTAINER_CLASS, futureExitScenarioMethod)}
          size={{ xs: 12 }}>
          <AllocationMethodTable
            approach={valuationsApproachFutureExit}
            spreadsheets={spreadsheets}
            onChange={onChange}
            isDisabled={isDisabled}
            approaches={approaches}
            financials={financials}
          />
        </Grid>

        {/* Future values per share */}
        {Boolean(futureValuesPerShare.length) && futureValuesPerShareSheet && (
          <Grid className={VALUATION_FUTURE_EXIT_FUTURE_VALUES_PER_SHARE_CONTAINER_CLASS}>
            <ValuesPerShareTable
              onChange={valuesPerShareOnChange}
              valuesPerShareSheet={futureValuesPerShareSheet}
              workbook={valuesPerShareWorkbook}
            />
          </Grid>
        )}

        {/* Present values per share */}
        {Boolean(presentValuesPerShare.length) && presentValuesPerShareSheet && (
          <Grid className={VALUATION_FUTURE_EXIT_PRESENT_VALUES_PER_SHARE_CONTAINER_CLASS}>
            <ValuesPerShareTable
              onChange={valuesPerShareOnChange}
              valuesPerShareSheet={presentValuesPerShareSheet}
              workbook={valuesPerShareWorkbook}
            />
          </Grid>
        )}

        <WidgetDrawer
          applicationContext={WidgetDrawerApplicationContext.FUTURE_EXIT}
          notesProps={{
            pageType: VALUATIONS_PAGE_VALUE,
            pageTypeKey: VALUATIONS_PAGE_KEY,
            pageTypeId: approach.id,
            notes,
            isApproach: true,
            setNotes,
            onAddNote,
            onUpdateNotes,
            onDeleteNote,
            isDisabled,
          }}
        />
      </CustomContainer>
    </FutureExitContext.Provider>
  );
};
export default FutureExit;
